import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { FiMapPin, FiTruck, FiCornerDownRight, FiX, FiMaximize2, FiInfo } from "react-icons/fi";
import { Modal, Button, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import "leaflet/dist/leaflet.css";

const GOOGLE_API_KEY = "AIzaSyD16h98hJe26HkkEvzKPbH2WytgT5srggM";

// Controller per un movimento fluido sulla mappa
const MapController = ({ coords, isFullscreen }) => {
  const map = useMap();
  useEffect(() => {
    if (coords) {
      map.flyTo(coords, isFullscreen ? 7 : 6, { animate: true, duration: 1.5 });
    }
  }, [coords, map, isFullscreen]);
  return null;
};

// Icone custom in stile "Glow Light" coerenti con il tema chiaro
const createIcon = (color, isGlow = false) => {
  return new L.DivIcon({
    html: `<div style="
            width: 12px; height: 12px; 
            border: 2px solid #fff; 
            border-radius: 50%; 
            background-color: ${color};
            box-shadow: ${isGlow ? `0 0 8px ${color}, 0 0 15px ${color}` : '0 2px 4px rgba(0,0,0,0.1)'};
          "></div>`,
    className: "",
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

const iconOrigin = createIcon("#6c757d"); // Grigio per fornitore
const iconDest = createIcon("#dc3545", true); // Rosso vivo con bagliore per il cliente

const MappaCrossDocking = () => {
  const [percorsi, setPercorsi] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);

  const getCoords = async (item, tipo) => {
    const prov = item[`PROVINCIA ${tipo}`];
    const naz = item[`NAZIONE ${tipo}`];
    const query = prov ? `${prov}, ${naz}` : naz;
    if (!query) return [45.0, 12.0];
    const cache = JSON.parse(localStorage.getItem("geocodeCache") || "{}");
    if (cache[query]) return cache[query];
    try {
      const res = await axios.get("https://maps.googleapis.com/maps/api/geocode/json", {
        params: { address: query, key: GOOGLE_API_KEY },
      });
      if (res.data.status === "OK") {
        const loc = res.data.results[0].geometry.location;
        const coords = [loc.lat, loc.lng];
        cache[query] = coords;
        localStorage.setItem("geocodeCache", JSON.stringify(cache));
        return coords;
      }
    } catch (e) { return [45.0, 12.0]; }
    return [45.0, 12.0];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`ordini_urgenti.json?nocache=${new Date().getTime()}`);
        const data = await res.json();
        let listaRaw = Array.isArray(data.CROSS_DOCKING) ? data.CROSS_DOCKING : (data.CROSS_DOCKING?.[0] || []);
        
        const validi = listaRaw.filter(item => item && item.Is_Totale === "0");
        const elaborati = [];
        for (let i = 0; i < validi.length; i++) {
          const item = validi[i];
          const start = await getCoords(item, "FORNITORE");
          const end = await getCoords(item, "CLIENTE");
          elaborati.push({ ...item, id_u: `${item.Doc_Cliente}-${i}`, start, end });
          setPercorsi([...elaborati]);
        }
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const selectedPath = useMemo(() => percorsi.find((p) => p.id_u === selectedId), [selectedId, percorsi]);

  const renderTooltipSpiegazione = (props) => (
    <Tooltip id="info-cross-tooltip" {...props}>
      <div style={{ textAlign: "left", fontSize: "0.75rem", padding: "4px" }}>
        Monitoraggio flussi <strong>Cross-Docking</strong>. Seleziona una tratta per calcolare la rotta e centrare il tracking sul punto di consegna finale.
      </div>
    </Tooltip>
  );

  // Sidebar condivisa sia per la card che per il modale
  const RenderSidebarContent = ({ isFullscreen = false }) => (
    <>
      <div className="mb-3 d-flex align-items-center justify-content-between">
        <div 
          className={`d-flex align-items-center ${!isFullscreen ? 'titolo-cliccabile' : ''}`}
          onClick={() => !isFullscreen && setShowFullscreenModal(true)}
          style={{ cursor: !isFullscreen ? "pointer" : "default" }}
          title={!isFullscreen ? "Espandi a tutto schermo" : ""}
        >
          <FiMapPin className="text-danger me-2 icona-pin" size={isFullscreen ? 18 : 16} />
          <span style={{ fontWeight: "800", fontSize: isFullscreen ? "0.85rem" : "0.75rem", color: "#475569", letterSpacing: "1.5px" }}>
            TRACKING ROTTE
          </span>
           <OverlayTrigger placement="bottom" overlay={renderTooltipSpiegazione}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", marginLeft: '5px' }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
        </div>
        {isFullscreen && <Badge bg="dark">{percorsi.length} TRATTE</Badge>}
      </div>
      
      {loading && (
        <div className="text-center my-auto">
          <div className="spinner-border spinner-border-sm text-danger mb-2"></div>
          <div className="text-muted small">Caricamento coordinate...</div>
        </div>
      )}
      
      <div className="flex-grow-1">
        {percorsi.map((p) => (
          <div
            key={p.id_u}
            onClick={() => setSelectedId(p.id_u)}
            className={`path-card p-3 mb-2 rounded-3 ${selectedId === p.id_u ? 'active' : ''}`}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-bold text-truncate text-dark" style={{ fontSize: isFullscreen ? '0.85rem' : '0.8rem', maxWidth: isFullscreen ? '260px' : '200px' }} title={p.Cliente}>
                {p.Cliente}
              </div>
              {isFullscreen ? (
                <Badge bg="light" text="dark" className="border font-monospace" style={{ fontSize: '0.65rem' }}>{p.Doc_Cliente}</Badge>
              ) : (
                <FiTruck size={14} className={selectedId === p.id_u ? 'text-danger' : 'text-muted'} />
              )}
            </div>
            <div className="text-muted d-flex align-items-center mt-1" style={{ fontSize: "0.68rem" }}>
              <FiCornerDownRight className="me-1 text-secondary" />
              <span className="text-truncate" title={p.Fornitore}>Da: {p.Fornitore}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  // Mappa condivisa sia per la card che per il modale
  const RenderMappaContent = ({ isFullscreen = false }) => (
    <MapContainer
      center={[45.0, 12.0]}
      zoom={isFullscreen ? 6 : 5}
      zoomControl={isFullscreen}
      style={{ height: "100%", width: "100%", backgroundColor: "#f1f5f9" }}
    >
      <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
      
      {percorsi.map((p) => (
        <React.Fragment key={p.id_u}>
          {(selectedId === p.id_u || !selectedId) && (
            <>
              <Marker position={p.start} icon={iconOrigin} />
              <Marker position={p.end} icon={iconDest}>
                <Popup>
                  <div style={{ padding: '2px' }}>
                    <strong style={{ color: '#dc3545', fontSize: '0.8rem' }}>{p.Cliente}</strong>
                    <div style={{ color: '#475569', fontSize: '0.7rem', marginTop: '4px' }}>
                      <b>Origine:</b> {p.Fornitore}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.65rem', marginTop: '2px' }}>
                      <b>Doc Cliente:</b> {p.Doc_Cliente}
                    </div>
                  </div>
                </Popup>
              </Marker>
              <Polyline
                positions={[p.start, p.end]}
                className="animated-polyline"
                pathOptions={{
                  color: selectedId === p.id_u ? "#dc3545" : "#94a3b8",
                  weight: selectedId === p.id_u ? (isFullscreen ? 4.5 : 3.5) : 1.5,
                  opacity: selectedId === p.id_u ? 1 : 0.5,
                }}
              />
            </>
          )}
        </React.Fragment>
      ))}
      {selectedPath && <MapController coords={selectedPath.end} isFullscreen={isFullscreen} />}
    </MapContainer>
  );

  return (
    <div className="shadow-sm card border-0 bg-white" style={{ 
      borderRadius: "16px", height: "350px", display: "flex", flexDirection: "row",
      overflow: "hidden", border: "1px solid #e2e8f0" 
    }}>
      <style>{`
        .sidebar-list::-webkit-scrollbar { width: 4px; }
        .sidebar-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        
        
        .path-card { 
          transition: all 0.2s ease; 
          border: 1px solid #e2e8f0;
          background-color: #ffffff;
          cursor: pointer;
        }
        .path-card:hover { 
          transform: translateX(4px); 
          background-color: #f8fafc;
        }
        .path-card.active { 
          border-color: #dc3545 !important; 
          background-color: #fff5f5;
          box-shadow: inset 4px 0 0 #dc3545;
        }
        
        @keyframes dash {
          to { stroke-dashoffset: 0; }
        }
        .animated-polyline {
          stroke-dasharray: 8;
          stroke-dashoffset: 160;
          animation: dash 4s linear infinite;
        }

        .leaflet-popup-content-wrapper {
          background: #ffffff !important;
          color: #334155 !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          border: 1px solid #e2e8f0;
        }
        .leaflet-popup-tip { background: #ffffff !important; }
        .leaflet-container a.leaflet-popup-close-button { color: #64748b !important; }
      `}</style>

      {/* SIDEBAR WIDGET */}
      <div className="sidebar-list d-flex flex-column bg-light" style={{ width: "280px", overflowY: "auto", borderRight: "1px solid #e2e8f0", padding: "16px" }}>
        <RenderSidebarContent isFullscreen={false} />
      </div>

      {/* MAPPA STANDARD + BOTTONE ESPANSIONE */}
      <div style={{ flex: 1, position: "relative", height: "100%" }}>
        <RenderMappaContent isFullscreen={false} />

        {/* Bottone per attivare la vista estesa */}
        <Button 
          variant="white" 
          onClick={() => setShowFullscreenModal(true)}
          className="shadow-sm border d-flex align-items-center justify-content-center p-0"
          style={{ position: "absolute", top: "12px", right: "12px", zIndex: 1000, width: "34px", height: "34px", borderRadius: "8px", backgroundColor: "#fff" }}
          title="Espandi mappa"
        >
          <FiMaximize2 size={15} className="text-secondary" />
        </Button>

        {/* Overlay Info Standard */}
        {selectedPath && (
          <div style={{
            position: 'absolute', bottom: '16px', left: '16px', zIndex: 1000,
            padding: '12px 16px', borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid #e2e8f0', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            backdropFilter: 'blur(8px)', minWidth: '240px'
          }}>
            <div className="text-danger fw-bold mb-1" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>DETTAGLIO TRATTA</div>
            <div className="text-dark fw-bold text-uppercase d-flex align-items-center flex-wrap" style={{ fontSize: '0.75rem' }}>
              <span className="text-truncate" style={{ maxWidth: '90px' }}>{selectedPath.Fornitore}</span> 
              <FiArrowRight className="mx-2 text-danger"/> 
              <span className="text-truncate" style={{ maxWidth: '90px' }}>{selectedPath.Cliente}</span>
            </div>
            <div className="text-muted mt-1 fw-semibold" style={{ fontSize: '0.6rem' }}>ORDINE: {selectedPath.Doc_Cliente}</div>
          </div>
        )}
      </div>

      {/* MODALE FULLSCREEN APERTO DA ESPANDI O CLICK SUL TITOLO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa", height: "100vh" }}>
          
          {/* Top Bar Modale */}
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 1100 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                CROSS-DOCKING MONITORING - VISTA ESTESA
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltipSpiegazione}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
              <FiX size={16} /> Chiudi Mappa
            </Button>
          </div>

          {/* Corpo Modale sdoppiato con Layout Orizzontale a tutto schermo */}
          <div className="d-flex flex-row flex-grow-1" style={{ overflow: "hidden" }}>
            
            {/* Sidebar Sinistra Espansa */}
            <div className="sidebar-list d-flex flex-column bg-light shadow-sm" style={{ width: "380px", overflowY: "auto", borderRight: "1px solid #e2e8f0", padding: "20px", zIndex: 1050 }}>
              <RenderSidebarContent isFullscreen={true} />
            </div>

            {/* Mappa Leaflet di Destinazione */}
            <div className="position-relative flex-grow-1 h-100">
              <RenderMappaContent isFullscreen={true} />

              {/* Box Tratta Selezionata Scalato in Basso a Sinistra */}
              {selectedPath && (
                <div style={{
                  position: 'absolute', bottom: '24px', left: '24px', zIndex: 1000,
                  padding: '16px 24px', borderRadius: '16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e2e8f0', 
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  backdropFilter: 'blur(12px)', minWidth: '350px'
                }}>
                  <div className="text-danger fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '1.2px' }}>VISTA FLUSSO LOGISTICO</div>
                  <div className="text-dark fw-bold text-uppercase d-flex align-items-center flex-wrap" style={{ fontSize: '0.95rem' }}>
                    <span className="text-truncate" style={{ maxWidth: '140px' }}>{selectedPath.Fornitore}</span> 
                    <FiArrowRight className="mx-2 text-danger" /> 
                    <span className="text-truncate" style={{ maxWidth: '140px' }}>{selectedPath.Cliente}</span>
                  </div>
                  <div className="mt-2 pt-2 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center">
                    <span className="text-muted fw-bold" style={{ fontSize: '0.7rem' }}>COD. DOCUMENTO:</span>
                    <Badge bg="danger" className="px-2.5 py-1.5">{selectedPath.Doc_Cliente}</Badge>
                  </div>
                </div>
              )}
            </div>

          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
};

const FiArrowRight = ({ className }) => (
  <svg className={className} stroke="currentColor" fill="none" strokeWidth="2.5" viewBox="0 0 24 24" height="1em" width="1em">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

export default MappaCrossDocking;